import React, { useEffect, useState } from "react";
import { ApplicationLayout, NotFoundState, Sidebar, Spinner } from "@rio-cloud/rio-uikit";
import Notification from "@rio-cloud/rio-uikit/Notification";
import { SidebarDetail } from "@components";
import { store } from "@store";
import { fetchAddressWithLatLong, fetchStopPublic } from "src/services/routes";
import { AddressSearch } from "src/types/routes";
import { IOnGoingTrip, IPublicStopShared, ITripStopItem } from "@types";
import { formatStopArrivalUtcToLocal } from "@utils";
import { Provider } from "react-redux";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";
import RioMap, {
    getBoundingBox,
    getRandomValueToForceRerender,
    ICoordinates,
} from "src/components/Map";
import { useIsMobile, usePublicRoutePolyline } from "@hooks";
import { config } from "src/config";
import { HeaderContent } from "../RouteView/components/HeaderContent";

const BOUNDING_BOX_PADDING = 0.02;
const MOBILE_MAP_HEIGHT = "45vh";

const mapPublicStop = (stop: IPublicStopShared['stop']): ITripStopItem => ({
    note: stop.stopName,
    label: stop.address,
    order: stop.order,
    routeStopId: stop.routeStopId,
    estimatedArrival: formatStopArrivalUtcToLocal(stop.estimatedArrival),
    actualArrival: formatStopArrivalUtcToLocal(stop.actualArrival),
    stopStatus: stop.status,
    category: stop.category,
    lat: stop.lat,
    lng: stop.lng,
});

const mapPublicStopDetail = (response: IPublicStopShared): IOnGoingTrip => ({
    tripId: '',
    routeId: '',
    driverId: '',
    driverName: '',
    destinyName: response.stop.address,
    assetId: response.vehicle?.assetId ?? '',
    assetName: response.vehicle?.assetName ?? '',
    distance: '',
    eta: '',
    routeStatusNow: response.routeStatus,
    tripStatusNow: response.isInterrupted ? 'INTERRUPTED' : response.tripStatus,
    actualProgress: '',
    polyline: response.currentPolyline ?? null,
    dateStarted: '',
    latLongActually: response.latLongActually ?? undefined,
    profileId: '',
});

const StopView = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selected, setSelected] = useState<IOnGoingTrip | null>();
    const [stops, setStops] = useState<ITripStopItem[]>([]);
    const [currencyAddress, setCurrencyAddress] = useState<AddressSearch>();
    const uuid = new URLSearchParams(document.location.search).get('link');
    const isMobile = useIsMobile();
    const {
        position,
        setPosition,
        markers,
        setMarkers,
        markerNow,
        currencySegments,
        boundingBox,
        setBoundingBox,
        locationNow,
        handleRoutes,
        handleVehiclePosition,
        send,
    } = usePublicRoutePolyline();

    const applyStopOnlyMap = (stop: IPublicStopShared['stop'], latLongActually: IPublicStopShared['latLongActually']) => {
        const stopPosition = { lat: stop.lat, lng: stop.lng };
        const points: ICoordinates[] = [stopPosition];

        if (latLongActually?.lat && latLongActually?.lng) {
            points.push(latLongActually);
        }

        setMarkers([
            {
                id: getRandomValueToForceRerender().toString(),
                markerProps: {
                    iconNames: ["rioglyph rioglyph-pushpin"],
                    name: stop.stopName,
                    markerColor: "bg-map-marker-route",
                    active: false,
                    fixed: false,
                },
                position: stopPosition,
            },
        ]);
        setPosition(stopPosition);

        const values = getBoundingBox(points);
        setBoundingBox({
            top: values.top + BOUNDING_BOX_PADDING,
            bottom: values.bottom - BOUNDING_BOX_PADDING,
            left: values.left - BOUNDING_BOX_PADDING,
            right: values.right + BOUNDING_BOX_PADDING,
        });
    };

    const applyResponse = (response: IPublicStopShared, initialLoad: boolean) => {
        const { stop, latLongActually } = response;
        const routes = response.polylinePlanned?.routes ?? [];

        setSelected(mapPublicStopDetail(response));
        setStops([mapPublicStop(stop)]);

        if (initialLoad) {
            if (routes.length) {
                handleRoutes(routes, [{ type: stop.stopName }]);
            } else {
                applyStopOnlyMap(stop, latLongActually);
            }
        }

        if (response.positions?.length) {
            send([...response.positions].reverse());
        } else if (latLongActually?.lat && latLongActually?.lng) {
            handleVehiclePosition(latLongActually);
        }
    };

    const fetchStopDetails = async () => {
        try {
            const response = await fetchStopPublic(uuid as string);
            applyResponse(response, true);
            setError(false);
        } catch (error) {
            console.log(error);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const updatedStop = async () => {
        try {
            const response = await fetchStopPublic(uuid as string);
            applyResponse(response, false);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSearchAddress = async () => {
        const response = await fetchAddressWithLatLong(selected?.latLongActually?.lat, selected?.latLongActually?.lng);
        setCurrencyAddress(response[0]);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(window.location.href)
            .then(() => Notification.info("URL copiada para a área de transferência!"))
            .catch(err => console.error("Erro ao copiar: ", err));
    };

    useEffect(() => {
        fetchStopDetails();

        if (!config.enablePolling) return;

        const interval = setInterval(() => {
            updatedStop();
        }, 30000);

        return () => {
            clearInterval(interval);
        };
    }, [uuid]);

    useEffect(() => {
        if (selected?.latLongActually) {
            handleSearchAddress();
        }
    }, [selected?.latLongActually?.lat, selected?.latLongActually?.lng]);

    const detailsContent = !error ? (
        <SidebarDetail
            currencyLocation={locationNow}
            handleCopy={handleCopy}
            currencyAddress={currencyAddress?.title}
            detail={selected}
            stops={stops}
            type='PUBLIC_STOP'
        />
    ) : (
        <div className="width-100pct height-100pct display-flex align-items-center justify-content-center">
            <NotFoundState
                headline="Página indisponível"
                message="Volte e tente novamente."
                fullWidth
                outerClassName="border-none"
            />
        </div>
    );

    const mapContent = (
        <RioMap
            zoom={12}
            position={position}
            boundingBox={boundingBox}
            apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
            segments={[]}
            currencySegments={
                currencySegments.length ? [{ points: currencySegments, alternative: false }] : []
            }
            markers={markerNow?.position?.lat ? [...markers, markerNow] : markers}
            useClusters={true}
        />
    );

    const desktopLayout = (
        <>
            <ApplicationLayout.Sidebar className="right height-100pct">
                <Sidebar
                    title="Parada"
                    headerClassName="padding-left-20"
                    bodyClassName="padding-x-20"
                    position={Sidebar.RIGHT}
                    disableClose
                    width={500}
                >
                    {detailsContent}
                </Sidebar>
            </ApplicationLayout.Sidebar>

            <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
                {mapContent}
            </ApplicationLayout.Body>
        </>
    );

    const mobileLayout = (
        <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct overflow-y-auto">
            <div className="width-100pct" style={{ height: MOBILE_MAP_HEIGHT }}>
                {mapContent}
            </div>
            <div className="bg-white padding-x-20 padding-bottom-20">
                <div className="text-size-h4 text-medium padding-y-15">Parada</div>
                {detailsContent}
            </div>
        </ApplicationLayout.Body>
    );

    return (
        <ApplicationLayout>
            <ApplicationLayout.Header>
                <HeaderContent />
            </ApplicationLayout.Header>
            {!loading ? (
                <ApplicationLayout.Body innerClassName="padding-0" className="padding-0 margin-0">
                    <Provider store={store}>
                        <MapDrawingProvider>
                            {isMobile ? mobileLayout : desktopLayout}
                        </MapDrawingProvider>
                    </Provider>
                </ApplicationLayout.Body>
            ) : (
                <div className="width-100pct height-100vh bg-white display-flex align-items-center justify-content-center">
                    <Spinner isDoubleSized text='Carregando...' />
                </div>
            )}
        </ApplicationLayout>
    );
};

export default StopView;
